// tooltips.ts

import BFText from "@/components/standard/BFText"
import PlusIcon from "@/assets/icons/plus-circle-dark.svg"
import { Platform, View, StyleSheet } from "react-native"

const styles = StyleSheet.create({
  iconContainer: {
    alignItems: "center",
    transform: [{ translateY: Platform.OS === "android" ? 5 : 3 }],
  },
})

export const tooltips = {
  costBasis: "Purchase price of the property plus rehab costs.",
  estimatedARV: "The property’s estimated sale price, after rehab.",
  estimatedCashToClose:
    "The estimated cash needed to purchase this property, given the loan terms entered.\n" +
    "\n" +
    "Note: This estimate does not include third party costs such as title or insurance. For a clear picture of your qualifying loan terms and all related costs, please submit a non-binding loan application to chat with one of our experts.",
  cashToClose:
    "The cash needed to purchase this property, given the loan terms.",
  estimatedRehabCosts:
    "Estimated Rehab Costs - The estimated expense of rehabilitating the property including costs incurred during construction.\n\nRehab Costs/Square Foot - The total estimated rehab costs divided by the total square footage.",
  grossProfitPotential:
    "Calculated by subtracting the total acquisition and rehab costs from the property's Estimated After Repair Value (ARV).",
  netProfitPotential:
    "Net profit after expenses associated with the purchase, rehab, and sale or lease of the property.",
  netProfitPotentialGraph: (
    <>
      The Potential Profit is the net profit after expenses associated with the
      purchase, rehab, and sale or lease of the property.
      {"\n\n"}Poor, Good, Great deal is based on the following ROI thresholds.
      ROI is calculated by dividing the profit by the cash to close plus
      expenses throughout the flip.
      {"\n\n"}Poor Deal {"<"} 10%
      {"\n"}Good Deal {"\u2265"} 10% {"<"} 20%
      {"\n"}Great Deal {"\u2265"} 20%
      {"\n\n"}
      <BFText
        style={{
          fontSize: 10,
          position: "relative",
          bottom: -2,
          lineHeight: 14,
        }}
      >
        *
      </BFText>{" "}
      The profit we provide is an estimate, calculated from estimates, the
      information you give us and projections about the future. While we aim for
      accuracy, we can&apos;t promise or guarantee these numbers.
    </>
  ),
  rentPotential: (
    <>
      The cash (mo) is the net cash per month expected from rental income after
      expenses associated with the ownership of the property.
      {"\n\n"}Poor Deal {"<"} 10%
      {"\n"}Good Deal {"\u2265"} 10% {"<"} 20%
      {"\n"}Great Deal {"\u2265"} 20%
      {"\n\n"}
      <BFText
        style={{
          fontSize: 10,
          position: "relative",
          bottom: -2,
          lineHeight: 12,
        }}
      >
        *
      </BFText>{" "}
      The monthly cash we provide is an estimate, calculated from estimates, the
      information you give us and projections about the future. While we aim for
      accuracy, we can&apos;t promise or guarantee these numbers.
    </>
  ),
  holdPeriod:
    "The amount of time you expect it to take to rehab and sell or rent the property.",
  holdingCosts:
    "Any holding costs incurred during the holding period. This can include utilities, HOA fees and other monthly costs associated with owning a property.",
  interestRate: "The annual interest rate charged on the loan amount.",
  interestRateMonthly:
    "The annual interest rate charged on the loan amount, and the resulting monthly payment.",
  leverage: "The percentage that Backflip is lending to the acquisition price.",
  monthlyPayment:
    "The monthly payment is the loan amount divided by the standard loan term (12 months).",
  initialMonthlyPayment:
    "For most loan products, interest accrues as funds are disbursed, so your monthly payment will increase as construction draws are made. This is your initial payment before any draws.",
  monthlyRevenue:
    "The anticipated monthly revenue from leasing the property to a tenant, once rehab is complete.",
  oneTimeCosts:
    "This can include fees and costs incurred on the transaction such as title, survey, and escrow costs, insurance premiums as well as costs associated with selling the property, such as staging costs, sales closing costs, contingencies, and brokerage commissions.\n\nCosts can include the following:\n- 2% of the Loan Amount for Estimated Title, Survey & Escrow\n- .75% of the Loan Amount for Estimated Insurance Premium\n- 6% of the ARV for agent\ncommissions",
  oneTimeFinancing:
    "One-time financing origination, appraisal, underwriting & legal review fees to be paid at close.",
  operatingExpenses:
    "The anticipated monthly expenses from operating the property after rehab is complete (e.g. - property taxes, insurance, association fees, maintenance).",
  purchasePrice:
    "Acquisition price of the property not including closing costs or fees.",
  purchasePriceRefi:
    "The price at which the property was originally purchased. If the property was inherited, the value of the property at the time it was inherited. ",
  rehabCostsPerSquareFoot:
    "Rehab Costs/Square Foot - The total estimated rehab costs divided by the total square footage.",
  rewardsCredits: "Refer your friends and save on your next deal.",
  rent: "The anticipated monthly revenue from leasing the property to a tenant, once rehab is complete.",
  estimatedAsIsPrice:
    "The estimated current market value of the property in its present condition, without any renovations or improvements.",
  compsSelected:
    "Comps are pulled from property records by an algorithm that factors in size, location, and feature similarities. The algorithm’s top three are selected as ‘Our Picks’.\n\n" +
    "Backflip currently supports comps for single family homes in the United States and comp coverage can vary in rural areas.",
  earnestMoneyDeposit:
    "A deposit made by the buyer to show commitment to the real estate transaction, typically applied towards the purchase price at closing.",
  totalLoanAmount:
    "The loan amount used to finance a portion of the investment, input as a percentage of the cost basis (purchase price + rehab cost).",
  estimatedAsIsValue:
    "The estimated market value of the property based on it's current condition.",
  constructionSpent:
    "The amount you have already spent on improvements to the property.",
  originalPurchaseDate:
    "The date the property was originally purchased. If the property was inherited, enter the date it was inherited.",
  existingLoanBalance:
    "The current principal balance on the loan, if you have a loan on the property.",
  cashToBorrower:
    " The estimated cash you will receive at closing, net of paying off the existing loan balance.\n\nNote: This is an estimate and does not include the outside title and insurance costs that will be added on the Final Settlement Statement.",
  cashFromBorrower:
    "The estimated cash you need to bring to closing, given the entered loan terms..\n\nNote: This is an estimate and does not include the outside title and insurance costs that will be added on the Final Settlement Statement.",
  marketMedianPrice:
    "The median final sale price of homes that sold during a given time period.",
  marketMedianDOM:
    "The median number of days homes that went under contract during a given period spent on the market before going under contract, meaning the seller accepted an offer from a buyer.\n\nExcludes homes that spent more than a year on the market before going under contract.",
  marketSaleToList:
    "Sale Price:List Price Ratio is a measurement of how close the typical home's final sale price was to its original list price. It's calculated as an average of the ratio of each home sold during a given period price divided by its list price. A sale-to-list price ratio of 99%, for instance, means a home sold for 1% under its list price. A sale-to-list price ratio of 101% means a home sold for 1% over its list price. This excludes properties with a sale price 50% or more above the listing price or 50% or more below the list price.",
  marketInventory:
    "End of Month (EOM): The total number of active listings on the market on the last day of a given time period.\n" +
    "\n" +
    "Sold: The total number of closed home sales.\n" +
    "\n" +
    "New: The total number of homes listed for sale during a given time period.",
  copilotCustomComps: "NEW! Filter comps for a more precise analysis!",
  copilotStep1SelectComps:
    "You'll find your analysis here once a comp is selected.",
  copilotStep2FindComps: "Find comps for the property here.",

  copilotStep3SelectComps: (
    <>
      Use{" "}
      <View style={styles.iconContainer}>
        <PlusIcon width={22} height={22} />
      </View>{" "}
      to select a comp to use in your analysis.
    </>
  ),

  copilotStep4CompsMap: "Find comps using the map by clicking here.",
  threeOrMoreComps: "Select 3 or more for precise estimates.",
}

export default tooltips
